/**
 * Thread collaboration state for the Collaborate control and the sidebar
 * "Shared" badge.
 *
 * Modelled on `state/browserPreview.ts`: a warm, per-thread cached atom fetches
 * `/api/amoreg/thread-collaboration`, and a single atom fetches the ids of
 * threads shared with the viewer. Mutations POST and then refresh the atoms —
 * all data flows through Effect atoms, never component `useEffect`/`useState`.
 *
 * @module state/collaboration
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

const Member = Schema.Struct({
  userId: Schema.String,
  email: Schema.String,
  firstName: Schema.NullOr(Schema.String),
  lastName: Schema.NullOr(Schema.String),
  role: Schema.String,
});
export type CollaborationMember = typeof Member.Type;

const CollaborationSchema = Schema.Struct({
  threadId: Schema.String,
  organizationId: Schema.String,
  ownerUserId: Schema.NullOr(Schema.String),
  viewerUserId: Schema.NullOr(Schema.String),
  isOwner: Schema.Boolean,
  canManage: Schema.Boolean,
  collaboratorUserIds: Schema.Array(Schema.String),
  members: Schema.Array(Member),
});
type CollaborationSnapshot = typeof CollaborationSchema.Type;

/** No collaboration feature on this server (endpoint 404 — plain T3 dev). */
const UNAVAILABLE: CollaborationSnapshot = {
  threadId: "",
  organizationId: "",
  ownerUserId: null,
  viewerUserId: null,
  isOwner: false,
  canManage: false,
  collaboratorUserIds: [],
  members: [],
};

const decodeCollaboration = Schema.decodeUnknownEffect(CollaborationSchema);

class CollaborationRequestError extends Schema.TaggedErrorClass<CollaborationRequestError>()(
  "CollaborationRequestError",
  { status: Schema.Number },
) {}

const fetchCollaboration = (threadId: string) =>
  Effect.gen(function* () {
    if (threadId.length === 0) return UNAVAILABLE;
    const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/thread-collaboration", { threadId });
    const response = yield* Effect.tryPromise(() => fetch(url, { credentials: "include" }));
    if (response.status === 404) return UNAVAILABLE;
    if (!response.ok) {
      return yield* Effect.fail(new CollaborationRequestError({ status: response.status }));
    }
    const json = yield* Effect.tryPromise(() => response.json());
    return yield* decodeCollaboration(json);
  });

const collaborationRuntime = Atom.runtime(Layer.empty);
const collaborationAtom = Atom.family((threadId: string) =>
  collaborationRuntime
    .atom(fetchCollaboration(threadId))
    .pipe(Atom.withLabel(`web-amoreg:collaboration:${threadId}`)),
);

const SharedIdsSchema = Schema.Struct({ threadIds: Schema.Array(Schema.String) });
const decodeSharedIds = Schema.decodeUnknownEffect(SharedIdsSchema);

const fetchSharedIds = Effect.gen(function* () {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/shared-thread-ids");
  const response = yield* Effect.tryPromise(() => fetch(url, { credentials: "include" }));
  if (response.status === 404) return { threadIds: [] as ReadonlyArray<string> };
  if (!response.ok) {
    return yield* Effect.fail(new CollaborationRequestError({ status: response.status }));
  }
  const json = yield* Effect.tryPromise(() => response.json());
  return yield* decodeSharedIds(json);
});

const sharedIdsRuntime = Atom.runtime(Layer.empty);
const sharedThreadIdsAtom = sharedIdsRuntime
  .atom(fetchSharedIds)
  .pipe(Atom.withLabel("web-amoreg:shared-thread-ids"));

export interface CollaborationView {
  readonly owner: CollaborationMember | null;
  readonly collaborators: readonly CollaborationMember[];
  /** Members who can still be added (org members minus owner and collaborators). */
  readonly addableMembers: readonly CollaborationMember[];
  readonly canManage: boolean;
  readonly isOwner: boolean;
  /** True when the viewer is a collaborator rather than the owner. */
  readonly isSharedWithViewer: boolean;
  readonly isPending: boolean;
  readonly refresh: () => void;
}

export function useThreadCollaboration(threadId: string): CollaborationView {
  const atom = useMemo(() => collaborationAtom(threadId), [threadId]);
  const result = useAtomValue(atom);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
    appAtomRegistry.refresh(sharedThreadIdsAtom);
  }, [atom]);

  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    const collaboratorIds = new Set(snapshot?.collaboratorUserIds ?? []);
    const members = snapshot?.members ?? [];
    const owner = members.find((member) => member.userId === snapshot?.ownerUserId) ?? null;
    return {
      owner,
      collaborators: members.filter((member) => collaboratorIds.has(member.userId)),
      addableMembers: members.filter(
        (member) =>
          member.userId !== snapshot?.ownerUserId && !collaboratorIds.has(member.userId),
      ),
      canManage: snapshot?.canManage ?? false,
      isOwner: snapshot?.isOwner ?? false,
      isSharedWithViewer:
        snapshot?.viewerUserId !== null &&
        snapshot?.viewerUserId !== undefined &&
        snapshot.viewerUserId !== snapshot.ownerUserId &&
        collaboratorIds.has(snapshot.viewerUserId),
      isPending: result.waiting && snapshot === null,
      refresh,
    };
  }, [result, refresh]);
}

/** The set of thread ids shared with the viewer, for the sidebar badge. */
export function useSharedThreadIds(): ReadonlySet<string> {
  const result = useAtomValue(sharedThreadIdsAtom);
  return useMemo(() => {
    const snapshot = Option.getOrNull(AsyncResult.value(result));
    return new Set(snapshot?.threadIds ?? []);
  }, [result]);
}

/** Add or remove a collaborator, then refresh the affected atoms. */
export async function mutateThreadCollaborator(
  threadId: string,
  userId: string,
  action: "add" | "remove",
): Promise<boolean> {
  const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/thread-collaboration", { threadId });
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ userId, action }),
  });
  appAtomRegistry.refresh(collaborationAtom(threadId));
  appAtomRegistry.refresh(sharedThreadIdsAtom);
  return response.ok;
}
